<script setup>
import { ref, inject } from 'vue';
import { useMemberStore } from '@/stores/member';
import { storeToRefs } from 'pinia';
import { trans } from 'laravel-vue-i18n';
import { useDateFormatter } from '@/composables/dateFormatter';
import { useAccessStore } from '@/stores/access';
import { router } from '@inertiajs/vue3';
import { capitalizeWords } from '@/utils/helper';

const { search, destroy } = useMemberStore();
const { all } = storeToRefs(useMemberStore());
const { formatDateTime } = useDateFormatter();
const { accessMember } = useAccessStore();

const params = ref({
    term: '',
    page: 1,
    sort_by: null,
    per_page: 10,
    status: null,
});

const searchMembers = () => {
    search({
        ...params.value,
    });
};

const modals = inject('modals');

const clearSearch = () => {
    params.value.term = '';
    searchMembers();
};

const tableUpdate = ({ page, sortBy, itemsPerPage }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage });
    searchMembers();
};

const remove = async (member) => {
    const confirm = await modals.show('ConfirmationModal', {
        title: trans('label.confirm_delete'),
        prompt: trans('label.are_you_sure_delete'),
    });

    if (confirm) {
        await destroy({ hash: member.hash });
        searchMembers();
    }
};

const openModal = (modal, mode = 'Create', member = null) => {
    modals.show(modal, {
        props: member ? {
            member: member,
            mode: mode
        } : {
            mode: mode,
        }
    }).then(() => {
        searchMembers();
    });
};

const access = async (member) => {
    const res = await accessMember(member.user.hash);
    if (res.data?.success) {
        router.visit(res.data.redirect);
    }
};
</script>

<template>
<MainContentLayout>
    <template #breadcrumb><BreadCrumb /></template>
    <template #title>{{ $t('label.management_title',{ 'what' : 'Member' }) }}</template>
    <template #actions>
        <StyledButton @click="openModal('MembersModal')">
            <FontAwesomeIcon icon="plus" class="mr-1" /> {{ $t('label.create') }}
        </StyledButton>
    </template>
    <div class="grid lg:grid-cols-3 md:gap-4 md:mb-4">
        <StyledSearch
            v-model="params.term"
            v-debounce:500ms="searchMembers"
            name="search"
            :label="$t('label.search')"
            :debounce-events="['input']"
            @click:clear="clearSearch" />
    </div>
    <DataTable
        v-if="all"
        :data="all"
        :headers="[
            { title: $t('label.name'), key: 'full_name', value: i => `${i.user?.first_name ?? ''} ${i.user?.last_name ?? ''}`.trim(), sortable: false },
            { title: $t('label.email'), key: 'email', value: i => i.user?.email ?? '-', sortable: false },
            { title: $t('label.phone_number'), key: 'phone_number', value: i => i.phone_number ?? '-', sortable: false },
            { title: $t('label.dob'), key: 'dob', value: i => i.dob, sortable: false },
            { title: $t('label.gender'), key: 'gender', value: i => capitalizeWords(i.gender) ?? '-', sortable: false },
            { title: $t('label.last_login'), key: 'last_login', value: i => i.user?.last_login_log ? `${formatDateTime(i.user.last_login_log.created_at)}` : 'n/a', sortable: false },
            { title: $t('label.status'), key: 'status', value: i => (i.user?.is_active ? $t('label.active') : $t('label.inactive')), sortable: false },
        ]"
        :actions="[
            {
                title: $t('label.edit'),
                icon: 'mdi-pencil',
                action: (item) => openModal('MembersModal', 'Edit', item),
            },
            {
                title: $t('label.access') ?? 'Access',
                icon: 'mdi-login',
                action: (item) => access(item),
            },
            {
                title: $t('label.delete'),
                icon: 'mdi-delete',
                action: (item) => remove(item),
            },
        ]"
        @table-update="tableUpdate" />
</MainContentLayout>
</template>
